import { getUiBasePath } from '@/utils/basePath';
import { PATHS } from '@/utils/paths';
import { ThemeModeProvider } from './providers/ThemeModeProvider';
import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntlProvider } from 'react-intl';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { PreviewBanner } from './components/PreviewBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RequireFeatureFlag } from './components/RequireFeatureFlag';
import { useIndexPage } from './hooks/useIndexPage';
import { AccessDeniedPage } from './pages/AccessDeniedPage';
import { AssetPage } from './pages/AssetPage';
import ChangelogPage from './pages/ChangelogPage';
import { ComparePage } from './pages/ComparePage';
import { MetadataBulkUploadPage } from './pages/MetadataBulkUploadPage';
import { ReportPage } from './pages/ReportPage';
import { ReportDetailPage } from './pages/ReportDetailPage';
import { MetadataPage } from './pages/MetadataPage/MetadataPage';
import { Oauth2RedirectPage } from './pages/Oauth2RedirectPage';
import { Oauth2SignoutPage } from './pages/Oauth2SignoutPage';
import { PartFinderPage } from './pages/PartFinderPage/PartFinderPage';
import { PartFinderReportPage } from './pages/PartFinderReportPage/PartFinderReportPage';
import { PartFinderBatchPage } from './pages/PartFinderBatchPage/PartFinderBatchPage';
import { DashboardPage } from './pages/DashboardPage/DashboardPage';
import { SearchPage, SearchRedirect } from './pages/SearchPage';
import SignInPage from './pages/SignInPage/SignInPage';
import { StatusPage } from './pages/StatusPage/StatusPage';
import { SuitableSubstitutesPage } from './pages/SuitableSubstitutesPage/SuitableSubstitutesPage';
import { TenantsPage } from './pages/TenantsPage';
import { TaxonomyPage } from './pages/TaxonomyPage/TaxonomyPage';
import { UsersPage } from './pages/UsersPage';
import { CspProvider } from './providers/CspProvider';
import { AssetUploadProvider } from './services/assetUpload/AssetUploadProvider';
import { AuthProvider } from './services/auth/AuthProvider';
import { CompareCartProvider } from './services/compareCart';
import { FeatureFlagsProvider } from './services/featureFlags/FeatureFlagsProvider';
import { FileUploadProvider } from './services/fileUpload';
import { TenantsProvider } from './services/tenants/TenantsProvider';
import { ToastProvider } from './services/toast/ToastProvider';
import { FeatureFlagName } from './utils/featureFlags';
import { installCoalescingScheduler, installObjectUrlRevoker, retry } from './utils/queryClient';

installCoalescingScheduler();

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_PRO_LICENSE_KEY);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry,
    },
  },
});

installObjectUrlRevoker(queryClient);

export default function App() {
  return (
    <CspProvider>
      <IntlProvider locale='en' defaultLocale='en'>
        <ThemeModeProvider>
          <PreviewBanner />
          <QueryClientProvider client={queryClient}>
            <Router basename={getUiBasePath()}>
              <TenantsProvider>
                <FeatureFlagsProvider>
                  <ToastProvider>
                    <FileUploadProvider>
                      <AssetUploadProvider>
                        <CompareCartProvider>
                          <AppRoutes />
                        </CompareCartProvider>
                      </AssetUploadProvider>
                    </FileUploadProvider>
                  </ToastProvider>
                </FeatureFlagsProvider>
              </TenantsProvider>
            </Router>
          </QueryClientProvider>
        </ThemeModeProvider>
      </IntlProvider>
    </CspProvider>
  );
}

export const AppRoutes = () => {
  const { indexPagePath } = useIndexPage();
  return (
    <AuthProvider>
      <Routes>
        <Route path={PATHS.SIGN_IN} element={<SignInPage />} />
        <Route path={PATHS.OAUTH2_REDIRECT} element={<Oauth2RedirectPage />} />
        <Route path={PATHS.OAUTH2_SIGNOUT} element={<Oauth2SignoutPage />} />
        <Route
          path='/*'
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path={PATHS.ACCESS_DENIED} element={<AccessDeniedPage />} />
                  <Route path={PATHS.TENANTS} element={<TenantsPage />} />
                  <Route path={PATHS.ASSETS} element={<SearchPage />} />
                  <Route path={PATHS.ASSET} element={<AssetPage />} />
                  <Route path={PATHS.REPORT} element={<ReportPage />} />
                  <Route path={PATHS.REPORT_DETAIL} element={<ReportDetailPage />} />
                  <Route path={PATHS.METADATA_BULK_UPLOAD} element={<MetadataBulkUploadPage />} />
                  <Route
                    path={PATHS.PART_FINDER}
                    element={
                      <RequireFeatureFlag flag={FeatureFlagName.PART_FINDER_REPORTS}>
                        <PartFinderPage />
                      </RequireFeatureFlag>
                    }
                  />
                  <Route
                    path={PATHS.PART_FINDER_REPORT}
                    element={
                      <RequireFeatureFlag flag={FeatureFlagName.PART_FINDER_REPORTS}>
                        <PartFinderReportPage />
                      </RequireFeatureFlag>
                    }
                  />
                  <Route
                    path={PATHS.PART_FINDER_BATCH}
                    element={
                      <RequireFeatureFlag flag={FeatureFlagName.PART_FINDER_REPORTS}>
                        <PartFinderBatchPage />
                      </RequireFeatureFlag>
                    }
                  />
                  <Route path={PATHS.SEARCH} element={<SearchRedirect />} />
                  <Route path={PATHS.CHANGELOG} element={<ChangelogPage />} />
                  <Route path={PATHS.COMPARE} element={<ComparePage />} />
                  <Route path={PATHS.DASHBOARD} element={<DashboardPage />} />
                  <Route path={PATHS.USERS} element={<UsersPage />} />
                  <Route path={PATHS.METADATA} element={<MetadataPage />} />
                  <Route path={PATHS.STATUS} element={<StatusPage />} />
                  <Route
                    path={PATHS.TAXONOMY}
                    element={
                      <RequireFeatureFlag flag={FeatureFlagName.TAXONOMY}>
                        <TaxonomyPage />
                      </RequireFeatureFlag>
                    }
                  />
                  <Route
                    path={PATHS.SUITABLE_SUBSTITUTES}
                    element={
                      <RequireFeatureFlag flag={FeatureFlagName.SUITABLE_SUBSTITUTES}>
                        <SuitableSubstitutesPage />
                      </RequireFeatureFlag>
                    }
                  />
                  <Route path='*' element={<Navigate to={indexPagePath} replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
};
